import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Renders an ApexCharts chart from a server-embedded JSON config.
//
// This controller is a faithful port of the reference dashboard's (tofu.misosil.com)
// chart renderer so our charts look pixel-identical. The server config carries extra,
// non-ApexCharts keys that this controller turns into real behaviour:
//   - options.{colors,stroke,annotations,chart_options,xaxis_type}  → ApexCharts options
//   - diff.{enabled,colors} + series[].diff.{data,percentage}       → day-over-day tooltip
//   - per-point { detail_url }                                       → click-through nav
//   - datetime x-axis                                               → M/D labels, JST, ja locale
//   - y-axis labels                                                 → compact (1.2K / 3.4M) numbers
//
// Backward compatible with the older flat schema (top-level colors/stroke/annotations and
// the `labels.format` "number"|"percent" sentinel) still emitted by some charts.

const MOBILE_BREAKPOINT = 640
const jp = (v) => new Intl.NumberFormat("ja-JP").format(v)
const isRateSeriesName = (name) => !!name && name.includes("エンゲージメント率")
const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[char]))

const JA_LOCALE = {
  name: "ja",
  options: {
    months: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"],
    shortMonths: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"],
    days: ["日曜日", "月曜日", "火曜日", "水曜日", "木曜日", "金曜日", "土曜日"],
    shortDays: ["日", "月", "火", "水", "木", "金", "土"],
  },
}

// Abbreviate axis tick numbers: 1234 → "1.2K", 3_400_000 → "3.4M" (matches reference).
function formatCompactNumber(value) {
  if (value === null || value === undefined || isNaN(value)) return value
  const abs = Math.abs(value)
  if (abs >= 1e9) return (value / 1e9).toFixed(1).replace(/\.0$/, "") + "B"
  if (abs >= 1e6) return (value / 1e6).toFixed(1).replace(/\.0$/, "") + "M"
  if (abs >= 1e3) return (value / 1e3).toFixed(1).replace(/\.0$/, "") + "K"
  return jp(value)
}

let clickableStylesInjected = false
function ensureClickableCursorStyles() {
  if (clickableStylesInjected) return
  clickableStylesInjected = true
  const style = document.createElement("style")
  style.textContent = ".apex-chart--clickable, .apex-chart--clickable * { cursor: pointer; }"
  document.head.appendChild(style)
}

export default class extends Controller {
  static values = { config: Object }

  connect() {
    if (!this.hasConfigValue) return
    ensureClickableCursorStyles()
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
    this.element.classList.remove("apex-chart--clickable")
  }

  renderChart() {
    const config = this.configValue
    // Only the new reference-schema charts (account 推移: have an `options` envelope and/or
    // `diff`) get the full reference treatment. Legacy flat configs (fb/yt/line/threads/
    // keyword/competitor/category/donut/heatmap) keep their original behaviour untouched.
    const useReference = !!config.options || !!(config.diff && config.diff.enabled)
    let options
    if (useReference) {
      this.ensureVisibleSeries(config)
      if (config.diff && config.diff.enabled) this.setupDiffTooltips(config)
      options = this.buildApexOptions(config)
    } else {
      options = this.buildLegacyOptions(config)
    }
    try {
      this.chart = new ApexCharts(this.element, options)
      this.chart.render().then(() => {
        this.applyHidden(config)
        if (useReference) this.moveAnnotationsToBack()
      })
    } catch (e) {
      console.error("[apex-chart] render error:", e)
    }
  }

  // Legacy flat-schema charts: original minimal behaviour — turn the `labels.format`
  // sentinel into a real formatter and wire per-point detail_url click-through.
  buildLegacyOptions(cfg) {
    const lastKnownMetadata = cfg.last_known_metadata
    const useLastKnownTooltip = cfg.last_known_tooltip && Array.isArray(lastKnownMetadata)
    delete cfg.last_known_metadata
    delete cfg.last_known_tooltip
    const yaxes = Array.isArray(cfg.yaxis) ? cfg.yaxis : cfg.yaxis ? [cfg.yaxis] : []
    yaxes.forEach((y) => {
      if (!y || !y.labels || !y.labels.format) return
      if (y.labels.format === "number") {
        y.labels.formatter = (v) => (v || v === 0 ? jp(Math.round(v)) : "0")
      } else if (y.labels.format === "percent") {
        y.labels.formatter = (v) => (v || v === 0 ? Number(v).toFixed(2) + "%" : "0%")
      }
    })
    cfg.chart = cfg.chart || {}
    cfg.chart.events = Object.assign({}, cfg.chart.events, {
      dataPointSelection: (_event, _ctx, opts) => {
        try {
          const series = opts.w.config.series[opts.seriesIndex]
          const point = series && series.data && series.data[opts.dataPointIndex]
          const url = point && point.detail_url
          if (url) typeof Turbo !== "undefined" ? Turbo.visit(url) : window.location.assign(url)
        } catch (_e) {
          /* no-op: a click without a detail_url is a no-op */
        }
      },
    })
    if (useLastKnownTooltip) {
      cfg.tooltip = {
        ...(cfg.tooltip || {}),
        shared: true,
        intersect: false,
        custom: this.lastKnownLegacyTooltip(cfg, lastKnownMetadata),
      }
    }
    return cfg
  }

  lastKnownLegacyTooltip(cfg, metadata) {
    return ({ series, dataPointIndex }) => {
      const category = cfg.xaxis && cfg.xaxis.categories
        ? cfg.xaxis.categories[dataPointIndex]
        : ""
      let html = `<div class="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[240px]">`
      html += `<div class="font-medium text-sm mb-2">${escapeHtml(category)}</div>`
      const items = cfg.series || []
      items.forEach((item, index) => {
        const value = series[index] && series[index][dataPointIndex]
        if (value === null || value === undefined) return
        const point = metadata[index] && metadata[index][dataPointIndex]
        const carriedLabel = point && point.imputed && point.source_date
          ? `<span class="text-[10px] text-amber-600 ml-1">前回取得値 (${escapeHtml(point.source_date)})</span>`
          : ""
        const color = escapeHtml((cfg.colors || [])[index] || "#6b7280")
        html += `<div class="flex items-center justify-between mb-1 gap-3"><div class="flex items-center"><div class="w-3 h-3 rounded-full mr-2" style="background-color:${color}"></div><span class="text-sm">${escapeHtml(item.name)}</span>${carriedLabel}</div><span class="font-bold">${jp(value)}</span></div>`
      })
      html += "</div>"
      return html
    }
  }

  // ---- option assembly ----------------------------------------------------

  buildApexOptions(config) {
    const opts = config.options || {}
    const chart = config.chart || {}
    const isHeatmap = chart.type === "heatmap"
    const isDatetime = opts.xaxis_type === "datetime" || (config.xaxis && config.xaxis.type === "datetime")
    const yaxisFormatter = this.resolveYAxisFormatter(opts.yaxis_label_formatter)

    const base = {
      chart: {
        type: chart.type,
        height: chart.height,
        id: chart.id,
        fontFamily: "inherit",
        toolbar: this.buildToolbarOptions(config),
      },
      series: config.series,
      colors: opts.colors || config.colors,
      xaxis: this.buildXAxis(config, isHeatmap, isDatetime),
      yaxis: this.buildYAxis(config, yaxisFormatter),
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        floating: false,
        itemMargin: { horizontal: 12, vertical: 6 },
        onItemHover: { highlightDataSeries: true },
      },
      grid: { borderColor: "#E5E7EB", strokeDashArray: 3, padding: { right: 24 } },
      tooltip: {
        theme: "light",
        shared: true,
        intersect: false,
        style: { fontSize: "12px", fontFamily: "inherit" },
        x: { formatter: (value) => this.formatTooltipDate(value, config) },
        y: { formatter: (value, ctx) => this.formatTooltipValue(value, ctx) },
      },
    }

    // chart_options may carry a custom tooltip (diff), legend overrides, etc.
    if (opts.chart_options) Object.assign(base, opts.chart_options)
    // annotations (weekend bands) — always applied last so they win.
    base.annotations =
      opts.annotations || (opts.chart_options && opts.chart_options.annotations) || config.annotations || {}

    if (isHeatmap) {
      base.plotOptions = config.plotOptions
      base.dataLabels = { enabled: true, style: { colors: ["#fff"] } }
      base.tooltip = { ...base.tooltip, custom: this.heatmapTooltip(config) }
      base.chart.toolbar.show = false
    } else {
      base.dataLabels = this.buildDataLabelsOptions(config)
      base.stroke = opts.stroke || config.stroke || { width: 2 }
    }

    if (isDatetime) {
      base.chart.defaultLocale = "ja"
      base.chart.locales = [JA_LOCALE]
    }

    base.responsive = [
      {
        breakpoint: MOBILE_BREAKPOINT,
        options: {
          chart: { toolbar: { show: false } },
          dataLabels: { style: { fontSize: "8px" } },
          legend: { fontSize: "10px", itemMargin: { horizontal: 6, vertical: 3 } },
          xaxis: { labels: { style: { fontSize: "9px" } } },
        },
      },
    ]

    this.attachDataPointNavigation(base, config)
    return base
  }

  buildToolbarOptions(config) {
    const chart = config.chart || {}
    const enabled = chart.type !== "heatmap"
    return {
      show: chart.toolbar && chart.toolbar.show === false ? false : enabled,
      tools: { download: true, zoom: enabled, zoomin: enabled, zoomout: enabled, pan: false, reset: enabled },
    }
  }

  buildXAxis(config, isHeatmap, isDatetime) {
    const x = { ...(config.xaxis || {}) }
    if (isDatetime && !x.type) x.type = "datetime"
    // one x tick per data point for dense datetime series (so every day shows, like reference)
    const n = config.series && config.series[0] && config.series[0].data ? config.series[0].data.length : 0
    if (isDatetime && n >= 10) x.tickAmount = n - 1
    const labels = { ...(config.xaxis && config.xaxis.labels), datetimeUTC: false }
    if (!isHeatmap) {
      labels.formatter = function (value) {
        const date = new Date(value)
        return isNaN(date.getTime()) ? value : `${date.getMonth() + 1}/${date.getDate()}`
      }
      if (labels.rotate === undefined) labels.rotate = -15
      if (labels.rotateAlways === undefined) labels.rotateAlways = true
    }
    x.labels = labels
    return x
  }

  buildYAxis(config, defaultFormatter) {
    const make = (axis) => {
      const a = { ...(axis || {}) }
      a.show = a.show !== undefined ? a.show : true
      const fmt = axis && axis.labels && axis.labels.format
      let formatter = defaultFormatter
      if (fmt === "number") formatter = (v) => (v || v === 0 ? jp(Math.round(v)) : "0")
      else if (fmt === "percent") formatter = (v) => (v || v === 0 ? Number(v).toFixed(2) + "%" : "0%")
      a.labels = {
        ...(axis && axis.labels),
        show: axis && axis.labels && axis.labels.show !== undefined ? axis.labels.show : true,
        formatter,
      }
      return a
    }
    if (Array.isArray(config.yaxis)) return config.yaxis.map(make)
    if (config.yaxis) return make(config.yaxis)
    return undefined
  }

  resolveYAxisFormatter(formatKey) {
    if (formatKey === "percentage_2") {
      return (value) => {
        const n = this.normalizeToNumber(value)
        return n === null ? value : `${n.toFixed(2)}%`
      }
    }
    return formatCompactNumber
  }

  // ---- tooltips -----------------------------------------------------------

  formatTooltipDate(value, config) {
    const date = new Date(value)
    if (isNaN(date.getTime())) return value
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    const h = String(date.getHours()).padStart(2, "0")
    const min = String(date.getMinutes()).padStart(2, "0")
    if (h !== "00" || min !== "00") return `${y}/${m}/${d} ${h}:${min}`
    return `${y}/${m}/${d}`
  }

  formatTooltipValue(value, ctx) {
    const name = ctx && ctx.w && ctx.w.globals.seriesNames[ctx.seriesIndex]
    if (isRateSeriesName(name)) return Number(value).toFixed(2) + "%"
    return jp(value)
  }

  // Diff tooltip: list every series value at the hovered point + its day-over-day change.
  setupDiffTooltips(config) {
    const opts = (config.options = config.options || {})
    const chartOpts = (opts.chart_options = opts.chart_options || {})
    chartOpts.tooltip = {
      shared: true,
      intersect: false,
      custom: ({ series, dataPointIndex, w }) => {
        let header
        try {
          const ts = w.globals.seriesX[0][dataPointIndex]
          const date = new Date(ts)
          const y = date.getFullYear()
          const m = String(date.getMonth() + 1).padStart(2, "0")
          const d = String(date.getDate()).padStart(2, "0")
          header = `${y}/${m}/${d}`
        } catch (_e) {
          header = `データポイント ${dataPointIndex + 1}`
        }
        let html = `<div class="p-3 bg-white rounded shadow min-w-[200px]"><div class="font-medium text-gray-600 mb-2">${header}</div>`
        config.series.forEach((s, i) => {
          if (series[i] === null || series[i] === undefined) return
          const value = series[i][dataPointIndex]
          if (value === null || value === undefined) return
          const color = (config.options.colors || config.colors || [])[i]
          const point = s.data && s.data[dataPointIndex]
          const carried = point && point.imputed
          const carriedLabel = carried && point.source_date
            ? `<span class="text-[10px] text-amber-600 ml-1">前回取得値 (${point.source_date})</span>`
            : ""
          html += `<div class="flex items-center justify-between mb-1"><div class="flex items-center"><div class="w-3 h-3 rounded-full mr-2" style="background-color:${color}"></div><span class="text-sm">${s.name}</span>${carriedLabel}</div><span class="font-bold">${jp(value)}</span></div>`
          if (s.diff && dataPointIndex > 0) {
            const delta = s.diff.data[dataPointIndex]
            const pct = s.diff.percentage[dataPointIndex]
            const dc = delta >= 0 ? config.diff.colors.positive : config.diff.colors.negative
            const icon = delta >= 0 ? "↑" : "↓"
            html += `<div class="text-xs ml-5 mb-1" style="color:${dc}">${icon} ${jp(Math.abs(delta))} (${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%)</div>`
          }
        })
        html += "</div>"
        return html
      },
    }
  }

  heatmapTooltip(config) {
    return ({ seriesIndex, dataPointIndex, w }) => {
      const point = config.series[seriesIndex].data[dataPointIndex]
      const xLabel = (point && point.x) || w.globals.labels[dataPointIndex]
      const yLabel = config.series[seriesIndex].name
      const value = point && point.value !== undefined ? point.value : point && point.y
      return `<div class="p-3 bg-white rounded shadow"><div class="font-medium">${yLabel}</div><div class="text-sm text-gray-600">${xLabel}</div><div class="text-lg font-bold">${jp(value)}</div></div>`
    }
  }

  // ---- data labels (with zero-suppression + sampling, matching reference) -------------

  buildDataLabelsOptions(config) {
    this.dataLabelSettings = { minSpacing: 5, relativeThreshold: 0.05, minAbsolute: 0, absoluteThreshold: null }
    this.setupDataLabelCaches(config)
    const controller = this
    return {
      enabled: true,
      formatter: (value, context) => {
        if (controller.shouldSuppressDataLabel(value, context)) return ""
        const name = context && context.w && context.w.globals.seriesNames[context.seriesIndex]
        if (isRateSeriesName(name)) {
          const n = controller.normalizeToNumber(value)
          return n === null ? `${value}%` : `${n.toFixed(2)}%`
        }
        const n = controller.normalizeToNumber(value)
        return n === null ? value : jp(n)
      },
      style: { fontSize: "10px", fontWeight: "bold" },
      offsetX: -8,
      offsetY: -5,
      textAnchor: "start",
      background: { enabled: false },
    }
  }

  setupDataLabelCaches(config) {
    const settings = this.dataLabelSettings
    const series = Array.isArray(config.series) ? config.series : []
    const valuesBySeries = series.map((s) => this.seriesNumericValues(s))
    const displayMap = valuesBySeries.map((values) => {
      const range = this.seriesRange(values)
      const threshold = this.calculateThreshold(range, settings)
      return this.computeDisplayFlags(values, settings, threshold)
    })
    this.dataLabelCache = { displayMap }
  }

  seriesNumericValues(series) {
    if (!series || !Array.isArray(series.data)) return []
    return series.data.map((point) => {
      if (point === null || point === undefined) return null
      if (typeof point === "number") return Number.isFinite(point) ? point : null
      if (Array.isArray(point)) return this.normalizeToNumber(point[1])
      if (typeof point === "object") return this.normalizeToNumber(point.y, point.value)
      return this.normalizeToNumber(point)
    })
  }

  seriesRange(values) {
    let min = null
    let max = null
    values.forEach((v) => {
      if (v === null) return
      if (min === null || v < min) min = v
      if (max === null || v > max) max = v
    })
    if (min === null || max === null) return 0
    return max - min
  }

  calculateThreshold(range, settings) {
    let threshold = 0
    if (Number.isFinite(range) && range > 0) threshold = range * settings.relativeThreshold
    if (settings.absoluteThreshold !== null) threshold = Math.max(threshold, settings.absoluteThreshold)
    if (settings.minAbsolute !== null && settings.minAbsolute !== undefined) threshold = Math.max(threshold, settings.minAbsolute)
    return threshold
  }

  computeDisplayFlags(values, settings, threshold) {
    const flags = new Array(values.length).fill(false)
    let lastIndex = null
    let lastValue = null
    let hasShown = false
    for (let i = 0; i < values.length; i += 1) {
      const value = values[i]
      if (value === null || value === undefined) continue
      if (Math.abs(value) <= 1e-9) continue
      let display = false
      if (!hasShown) {
        display = true
      } else {
        const diff = lastValue === null ? null : Math.abs(value - lastValue)
        const significant = this.isSignificantDifference(diff, threshold)
        const spaced = settings.minSpacing > 0 ? (lastIndex === null ? true : i - lastIndex >= settings.minSpacing) : false
        const extreme = this.isLocalExtreme(values, i, threshold)
        const last = this.isLastNonNull(values, i)
        display = significant || spaced || extreme || last
      }
      if (display) {
        flags[i] = true
        lastIndex = i
        lastValue = value
        hasShown = true
      }
    }
    return flags
  }

  isSignificantDifference(diff, threshold) {
    if (diff === null || diff === undefined) return false
    if (threshold === null || threshold === undefined || threshold <= 0) return diff > 0
    return diff >= threshold
  }

  isLocalExtreme(values, index, threshold) {
    const cur = values[index]
    if (cur === null || cur === undefined) return false
    const prevIdx = this.findValueIndex(values, index, -1)
    const nextIdx = this.findValueIndex(values, index, 1)
    if (prevIdx === null || nextIdx === null) return false
    const prev = values[prevIdx]
    const next = values[nextIdx]
    if (prev === null || next === null) return false
    const higher = cur > prev && cur > next
    const lower = cur < prev && cur < next
    if (!higher && !lower) return false
    return this.isSignificantDifference(Math.abs(cur - prev), threshold) || this.isSignificantDifference(Math.abs(cur - next), threshold)
  }

  findValueIndex(values, index, step) {
    for (let i = index + step; i >= 0 && i < values.length; i += step) {
      if (values[i] !== null && values[i] !== undefined) return i
    }
    return null
  }

  isLastNonNull(values, index) {
    for (let i = index + 1; i < values.length; i += 1) {
      if (values[i] !== null && values[i] !== undefined) return false
    }
    return true
  }

  shouldSuppressDataLabel(value, context) {
    const { seriesIndex, dataPointIndex } = context || {}
    const map = this.dataLabelCache && this.dataLabelCache.displayMap && this.dataLabelCache.displayMap[seriesIndex]
    if (Array.isArray(map)) return !map[dataPointIndex]
    const n = this.normalizeToNumber(value)
    if (n === null) return false
    return Math.abs(n) <= 1e-9
  }

  normalizeToNumber(...candidates) {
    for (const c of candidates) {
      if (c === null || c === undefined) continue
      if (typeof c === "number") {
        if (Number.isFinite(c)) return c
        continue
      }
      if (typeof c === "string") {
        const cleaned = c.trim().replace(/[,\s]/g, "").replace(/%$/, "")
        if (cleaned === "") continue
        const parsed = Number(cleaned)
        if (Number.isFinite(parsed)) return parsed
      }
    }
    return null
  }

  // ---- click-through navigation -------------------------------------------

  attachDataPointNavigation(options, config) {
    if (!config || !Array.isArray(config.series) || config.series.length === 0) return
    if (config.chart && config.chart.type === "heatmap") return
    options.chart = options.chart || {}
    const existing = options.chart.events || {}
    const navigate = (_event, _ctx, dp) => this.handleDataPointNavigation(dp, config)
    const pointer = (_event, ctx, dp) => {
      const has = !!(dp && this.extractDetailUrl(config, dp.seriesIndex, dp.dataPointIndex))
      this.element.classList.toggle("apex-chart--clickable", has)
    }
    const leave = () => this.element.classList.remove("apex-chart--clickable")
    const lifecycle = () => this.moveAnnotationsToBack()
    options.chart.events = {
      ...existing,
      dataPointSelection: navigate,
      markerClick: navigate,
      dataPointMouseEnter: pointer,
      dataPointMouseLeave: leave,
      mounted: lifecycle,
      updated: lifecycle,
    }
  }

  handleDataPointNavigation(dp, config) {
    if (!dp) return
    const url = this.extractDetailUrl(config, dp.seriesIndex, dp.dataPointIndex)
    if (!url) return
    typeof Turbo !== "undefined" ? Turbo.visit(url) : window.location.assign(url)
  }

  extractDetailUrl(config, seriesIndex, dataPointIndex) {
    const series = config.series && config.series[seriesIndex]
    const point = series && series.data && series.data[dataPointIndex]
    return point && point.detail_url
  }

  // ---- post-render housekeeping -------------------------------------------

  ensureVisibleSeries(config) {
    if (!config.series || config.series.length === 0) return
    if (config.series.every((s) => s.hidden === true)) config.series[0].hidden = false
  }

  applyHidden(config) {
    ;(config.series || []).forEach((s) => {
      if (s && s.hidden) {
        try {
          this.chart.hideSeries(s.name)
        } catch (_e) {
          /* series may not exist yet on very fast re-renders */
        }
      }
    })
  }

  // Push weekend-band annotations behind the data series (z-order fix).
  moveAnnotationsToBack() {
    if (!this.chart || !this.chart.el) return
    const graphical = this.chart.el.querySelector(".apexcharts-inner .apexcharts-graphical")
    if (!graphical) return
    const annotations = graphical.querySelector(".apexcharts-annotations")
    if (annotations && graphical.firstChild !== annotations) graphical.insertBefore(annotations, graphical.firstChild)
  }
}
